import React, { memo, FC, useMemo } from 'react';
import _ from 'underscore';
import {
  Button,
  Caption1,
  Caption1Stronger,
  Caption2,
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridCell,
  DataGridHeader,
  Image,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import classNames from 'classnames';
import { OverviewCardFooter, usePaginationConfig, IFooterButtonConfig } from '@bingads-webui-campaign-react/overview-card-footer';

import { IPerformanceData, IColumnData, IMetricFormatter } from './types';
import { MetricPicker } from './metric-picker';
import { getColumns } from './columns';
import { TableShimmer } from './table-shimmer';
import { EntityNameColumnId, InsightColumnId, ChangeBarColumnId } from './const';
import { getComparisonText } from './get-comparison-text';
import SearchExclamationImage from '../media/search-exclamation-image.png';

export const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  entityNameColumn: {
    flexGrow: 1.8,
  },
  insightColumn: {
    flexGrow: 0.3,
  },
  barColumn: {
    flexGrow: 2,
  },
  contentHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  popover: {
    width: '300px',
  },
  noDataContainer: {
    flexGrow: 1,
    textAlign: 'center',
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
});

export interface IOverviewTopChangesProps {
  isLoading?: boolean;
  selectedMetric: string;
  data?: IPerformanceData[];
  i18n: any;
  metricFormatter: IMetricFormatter;
  footerButtonConfigs?: IFooterButtonConfig[];
  getHref: Function;
  getPopoverContent?: Function;
  entityType: string;
  metrics: Array<{
    key: string,
    displayName: string,
  }>;
  onMetricSelect: (key: string) => void;
  currentDateRange: {
    end: Date;
    start: Date;
  };
  comparisonDateRange: {
    end: Date;
    start: Date;
  };
  renderFluctuationPopup: Function;
}

export const OverviewTopChanges: FC<IOverviewTopChangesProps> = memo((props: IOverviewTopChangesProps) => {
  const {
    isLoading,
    selectedMetric,
    data,
    i18n,
    metricFormatter,
    footerButtonConfigs,
    getHref,
    getPopoverContent,
    entityType,
    metrics,
    onMetricSelect,
    currentDateRange,
    comparisonDateRange,
    renderFluctuationPopup,
  } = props;
  const classes = useStyles();

  const columns = getColumns({
    selectedMetric,
    metricFormatter,
    currentDateRange,
    comparisonDateRange,
    renderFluctuationPopup,
    metrics,
    i18n,
  });

  const {
    getDataToDisplayFromSortedData,
    renderPagination,
    state: paginationState,
  } = usePaginationConfig<IPerformanceData>({
    data,
    i18n,
    isLoading,
  });

  const dataToDisplay = useMemo(() => getDataToDisplayFromSortedData(data), [getDataToDisplayFromSortedData, data]);

  const maxChange = useMemo(() => _.max(_.map(data, item => Math.abs(item.PerformanceMetricsChange[selectedMetric]))) || 0, [data, selectedMetric]);

  if (isLoading || !paginationState?.initialized) {
    return <TableShimmer />;
  }

  const comparisonText = getComparisonText(i18n, comparisonDateRange);

  return (
    <div className={classNames('overview-top-changes', classes.container)}>
      <div className={classes.contentHeaderContainer}>
        <span className={classes.dateContainer}>
          <Caption2 className="compared-date-range" aria-label={comparisonText}>
            {comparisonText}
          </Caption2>
          <Popover>
            <PopoverTrigger disableButtonEnhancement>
              <Button size="small" appearance="transparent" icon={<Info16Regular />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
              <div className={classNames('info-popover', classes.popover)}>
                {i18n.getString(_TL_('Change the date range of this tile by using the date selector in the upper right-hand corner of the Overview page.'))}
              </div>
            </PopoverSurface>
          </Popover>
        </span>

        <MetricPicker
          metrics={metrics}
          onMetricSelect={onMetricSelect}
          selectedMetricKey={selectedMetric}
        />
      </div>
      {_.isEmpty(dataToDisplay) &&
        <div className={classNames('no-data-container', classes.noDataContainer)}>
          <div className={classNames('ba-overview-top-changes-no-data-image')}>
            <Image src={SearchExclamationImage} height={50} width={50} /><br />
          </div>
          <Caption1Stronger style={{ display: 'block', marginTop: '1px' }}>{i18n.getString(_TL_('No data available. '))}</Caption1Stronger><br />
          <div className={classNames('ba-overview-top-changes-no-data-available-text')}>
            <Caption1 style={{ display: 'block', marginTop: '1px' }}>
              {i18n.getString(_TL_('To update the tile, select a different date range.'))}
            </Caption1>
            <Caption1 style={{ display: 'block', marginTop: '1px' }}>
              {i18n.getString(_TL_('If the tile remains blank, your account might have no data.'))}
            </Caption1>
          </div>
        </div>
      }
      {!_.isEmpty(dataToDisplay) &&
        <DataGrid
          items={dataToDisplay}
          columns={columns}
          getRowId={item => item.Id}
          className={classNames('ba-top-changes-table', classes.grid)}
        >
          <DataGridHeader /* DataGridHeader is required (from documentation), if not added a bug happens when metric is updated */ />
          <DataGridBody<IColumnData>>
            {({ item, rowId }) => (
              <DataGridRow<IColumnData> key={rowId}>
                {({ renderCell, columnId }) => {
                  const columnData = {
                    ...item,
                    href: getHref(`${item.Id}`, item.CampaignType),
                    columnConfig: { columnId },
                    getPopoverContent,
                    entityType,
                    maxChange,
                  };
                  return (
                    <DataGridCell
                      className={classNames(
                        `ba-top-changes-table-column-${columnId}`,
                        { [classes.entityNameColumn]: columnId === EntityNameColumnId },
                        { [classes.insightColumn]: columnId === InsightColumnId },
                        { [classes.barColumn]: columnId === ChangeBarColumnId }
                      )}
                      aria-label={i18n.getString(_TL_('Column {{columnId}}'), { columnId })}
                    >
                      {renderCell(columnData)}
                    </DataGridCell>
                  );
                }}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      }
      <OverviewCardFooter footerButtonConfigs={footerButtonConfigs} renderPagination={renderPagination} />
    </div>
  );
});

OverviewTopChanges.defaultProps = {
  isLoading: false,
  data: [],
  footerButtonConfigs: null,
  getPopoverContent: undefined,
};
